import React, { forwardRef, createElement, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import cs from 'classnames';
import Icon from '../icons/index';
require("./index.css");
var styles = {
  "accordionItem": "accordionItem-1CxFm",
  "accordionItemv2": "accordionItemv2-3CC_J",
  "accordionItemHeadingWrapper": "accordionItemHeadingWrapper-1-b10",
  "defaultTypeStyle-body-1": "defaultTypeStyle-body-1-macfL",
  "expanded": "expanded-3G0tl",
  "defaultTypeStyle-xsmall": "defaultTypeStyle-xsmall-xu3FE",
  "defaultTypeStyle-small": "defaultTypeStyle-small-114PX",
  "defaultTypeStyle-medium": "defaultTypeStyle-medium-3CpaE",
  "mobileTypeStyle-body-1": "mobileTypeStyle-body-1-19Awb",
  "mobileTypeStyle-xsmall": "mobileTypeStyle-xsmall-2wj4h",
  "mobileTypeStyle-small": "mobileTypeStyle-small-3P-ae",
  "mobileTypeStyle-medium": "mobileTypeStyle-medium-2oHSk",
  "accordionItemHeading": "accordionItemHeading-3bBXZ",
  "noPanel": "noPanel-2fW6T",
  "accordionItemHeadingIcon": "accordionItemHeadingIcon-oN4oZ",
  "accordionItemToggle": "accordionItemToggle-1r17a",
  "accordionItemToggleIcon": "accordionItemToggleIcon-I0m5H",
  "iconExpanded": "iconExpanded-S4qVA",
  "accordionItemPanelContent": "accordionItemPanelContent-HFI5h",
  "accordionItemPanel": "accordionItemPanel-1Xcvv",
  "accordionItemv1": "accordionItemv1-2K8Nx",
  "accordionItemLarge": "accordionItemLarge-kAIlo",
  "accordionItemMedium": "accordionItemMedium-32pDW",
  "accordionNoShadow": "accordionNoShadow-1Hdpx"
};
import ToggleIcon from './toggleIcon';
var AccordionPanel = /*#__PURE__*/forwardRef((_ref, ref) => {
  var {
    accordionId,
    analyticsId,
    children,
    className,
    defaultTypeStyle,
    expanded,
    handleClick,
    heading,
    headingClassName,
    headingTag,
    headingWrapperClassName,
    icon,
    mobileTypeStyle,
    noShadow,
    panelContentClassName,
    panelContentRef,
    size,
    stateKey,
    v2,
    onBeforeOpen,
    onAfterOpen,
    onBeforeClose,
    onAfterClose
  } = _ref;
  var detailsElement = useRef();
  var summaryElement = useRef(ref);
  var contentElement = useRef(panelContentRef);
  var [isOpen, setIsOpen] = useState(expanded);
  var animation = null;
  var isClosing = false;
  var isExpanding = false;
  useEffect(() => {
    setIsOpen(expanded);
  }, [expanded]);
  var shrink = () => {
    isClosing = true;
    setIsOpen(false);
    var startHeight = "".concat(detailsElement.current.offsetHeight, "px");
    var endHeight = "".concat(summaryElement.current.offsetHeight, "px");
    if (animation) {
      animation.cancel();
    }
    // Start a WAAPI animation
    if (detailsElement.current.animate) {
      animation = detailsElement.current.animate({
        height: [startHeight, endHeight]
      }, {
        duration: 400,
        easing: 'ease-out',
        fill: 'forwards'
      });
      animation.onfinish = () => {
        handleClick({
          event,
          stateKey,
          expanded
        });
        onAnimationFinish();
        if (onAfterClose) {
          onAfterClose();
        }
      };
      animation.oncancel = () => isClosing = false;
    } else {
      handleClick({
        event,
        stateKey,
        expanded
      });
      onAnimationFinish();
    }
  };
  var open = () => {
    detailsElement.current.style.height = "".concat(detailsElement.current.offsetHeight, "px");
    handleClick({
      event,
      stateKey,
      expanded
    });
    window.requestAnimationFrame(() => expand());
  };
  var expand = () => {
    isExpanding = true;
    setIsOpen(true);
    var startHeight = "".concat(summaryElement.current.offsetHeight, "px");
    var endHeight = "".concat(summaryElement.current.offsetHeight + contentElement.current.offsetHeight, "px");
    if (animation) {
      animation.cancel();
    }
    // Start a WAAPI animation
    if (detailsElement.current.animate) {
      animation = detailsElement.current.animate({
        height: [startHeight, endHeight]
      }, {
        duration: 400,
        easing: 'ease-out',
        fill: 'forwards'
      });
      animation.onfinish = () => {
        onAnimationFinish();
        if (onAfterOpen) {
          onAfterOpen();
        }
      };
      animation.oncancel = () => isExpanding = false;
    } else {
      onAnimationFinish();
    }
  };
  var onAnimationFinish = () => {
    setTimeout(() => {
      var _detailsElement$curre;
      // cancel animation so the fill option is cleared, resetting the height
      if (animation) {
        animation.cancel();
      }
      animation = null;
      isClosing = false;
      isExpanding = false;
      if (detailsElement !== null && detailsElement !== void 0 && (_detailsElement$curre = detailsElement.current) !== null && _detailsElement$curre !== void 0 && _detailsElement$curre.style) {
        detailsElement.current.style.height = detailsElement.current.style.overflow = '';
      }
    }, 0);
  };
  var onClick = event => {
    event.preventDefault();
    if (handleClick) {
      detailsElement.current.style.overflow = 'hidden';
      if (isClosing || !expanded) {
        if (onBeforeOpen) {
          onBeforeOpen();
        }
        open();
      } else if (isExpanding || expanded) {
        if (onBeforeClose) {
          onBeforeClose();
        }
        shrink();
      }
    }
  };
  return /*#__PURE__*/React.createElement("details", {
    className: cs(styles.accordionItem, {
      [className]: className,
      [styles.accordionItemLarge]: !v2 && size === 'large',
      [styles.accordionItemMedium]: !v2 && size === 'medium',
      [styles.accordionNoShadow]: !v2 && noShadow,
      [styles.accordionItemv1]: !v2,
      [styles.accordionItemv2]: v2
    }),
    "data-testid": "accordion-item-".concat(stateKey),
    open: expanded,
    "data-isexpanded": JSON.stringify(expanded),
    id: "".concat(accordionId, "-").concat(stateKey),
    ref: detailsElement
  }, /*#__PURE__*/React.createElement("summary", {
    tabIndex: "0",
    role: "button",
    "aria-expanded": expanded,
    "aria-controls": "".concat(accordionId, "-accordion-item__panel-").concat(stateKey),
    id: "".concat(accordionId, "-accordion-item-heading-wrapper-").concat(stateKey),
    className: cs(styles.accordionItemHeadingWrapper, {
      // 'lll-grid': size === 'large',
      [styles.accordionItemLarge]: !v2 && size === 'large',
      [styles.accordionItemMedium]: !v2 && size === 'medium',
      [styles.accordionNoShadow]: !v2 && noShadow,
      [styles["defaultTypeStyle-".concat(defaultTypeStyle)]]: defaultTypeStyle,
      [styles["mobileTypeStyle-".concat(mobileTypeStyle)]]: mobileTypeStyle,
      [headingWrapperClassName]: headingWrapperClassName,
      [styles.expanded]: expanded
    }),
    "data-lulu-track": analyticsId && "".concat(analyticsId, "-").concat(stateKey),
    "data-testid": "accordion-item-heading-wrapper-".concat(stateKey),
    onClick: onClick,
    ref: summaryElement
  }, /*#__PURE__*/createElement(headingTag, {
    className: cs(styles.accordionItemHeading, {
      [headingClassName]: headingClassName
    }),
    'data-testid': "accordion-item-heading-".concat(stateKey),
    id: "".concat(accordionId, "-accordion__heading-").concat(stateKey)
  }, /*#__PURE__*/React.createElement(React.Fragment, null, icon && /*#__PURE__*/React.createElement(Icon, {
    "data-testid": "icon",
    content: icon,
    className: styles.accordionItemHeadingIcon,
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement("span", {
    className: "accordionItemHeadingTitle"
  }, heading))), /*#__PURE__*/React.createElement(ToggleIcon, {
    expanded: isOpen
  })), /*#__PURE__*/React.createElement("div", {
    className: cs(styles.accordionItemPanel, {
      panelContentClassName
    }),
    role: "region",
    "aria-hidden": !expanded,
    id: "".concat(accordionId, "-accordion-item__panel-").concat(stateKey),
    "aria-labelledby": "".concat(accordionId, "-accordion__heading-").concat(stateKey),
    "data-testid": "panel-".concat(stateKey),
    ref: contentElement
  }, /*#__PURE__*/React.createElement("div", {
    className: cs(styles.accordionItemPanelContent),
    "data-testid": "panel-content-".concat(stateKey),
    ref: panelContentRef
  }, children)));
});
AccordionPanel.propTypes = {
  accordionId: PropTypes.string.isRequired,
  analyticsId: PropTypes.string,
  children: PropTypes.node.isRequired,
  className: PropTypes.string.isRequired,
  defaultTypeStyle: PropTypes.string.isRequired,
  expanded: PropTypes.bool.isRequired,
  handleClick: PropTypes.func,
  heading: PropTypes.node.isRequired,
  headingClassName: PropTypes.string.isRequired,
  headingTag: PropTypes.string,
  headingWrapperClassName: PropTypes.string.isRequired,
  icon: PropTypes.node,
  mobileTypeStyle: PropTypes.string.isRequired,
  noShadow: PropTypes.bool.isRequired,
  panelContentClassName: PropTypes.string.isRequired,
  panelContentRef: PropTypes.func,
  size: PropTypes.string.isRequired,
  stateKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  v2: PropTypes.bool.isRequired,
  /** function is called before the open animation starts */
  onBeforeOpen: PropTypes.func,
  /** function is called after the open animation finishes */
  onAfterOpen: PropTypes.func,
  /** function is called before the close animation starts */
  onBeforeClose: PropTypes.func,
  /** function is called after the close animation finishes*/
  onAfterClose: PropTypes.func
};
AccordionPanel.defaultProps = {
  headingTag: 'h3',
  handleClick: null,
  analyticsId: null,
  icon: null,
  panelContentRef: null,
  stateKey: null,
  onBeforeOpen: null,
  onAfterOpen: null,
  onBeforeClose: null,
  onAfterClose: null
};
export default AccordionPanel;